import { useCallback } from 'react';

import { useUpdateVaultInfo } from '@services/api/vaults/vaults.api';
import { usePublish } from '@services/contract/vault/vault.contract';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { vaultActions } from '@contexts/vaults/VaultContext';

import { VaultDto } from '@dto/vaults/vaults.dto';

import { useSignMessage } from '../common/useSignMessage';

const usePublishVaultAction = (vault: VaultDto) => {
  const { account } = useAuth();
  const { signData, isPending: isSignPending } = useSignMessage();
  const { mutateAsync: updateVaultInfo, isPending: isUpdatePending } =
    useUpdateVaultInfo();

  const handleUpdate = async () => {
    await vaultActions.onUpdateVault();
  };

  const { writeContract, isPending } = usePublish(vault, handleUpdate);

  const sign = useCallback(
    async (title: string, publisherName: string, description: string) => {
      try {
        if (!account) return;

        const data = {
          vaultId: vault.id,
          title,
          description,
          publisherName,
          timestamp: Date.now(),
        };

        const signature = await signData(data);

        await updateVaultInfo({ data, signature });
        await handleUpdate();
      } catch (e) {
        console.error('usePublishVaultAction', e);
      }
    },
    [account, signData, updateVaultInfo, vault],
  );

  const publish = useCallback(
    async (title: string, publisherName: string, description: string) => {
      await sign(title, description, publisherName);

      await writeContract();
    },
    [sign, writeContract],
  );

  return {
    publish,
    sign,
    isPending: isPending || isUpdatePending || isSignPending,
  };
};

export default usePublishVaultAction;
